import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { auth } from "../firebase";
import { createUserWithEmailAndPassword, signOut } from "firebase/auth";

const LOOKUP_URL = "https://us-central1-lastwishbox-business.cloudfunctions.net/getTenantInvite";

export default function AcceptTenantInvite() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = useMemo(() => String(params.get("token") || "").trim(), [params]);

  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");

  useEffect(() => {
    let ignore = false;

    async function loadInvite() {
      if (!token) {
        if (!ignore) {
          setMsg("Invite token is missing.");
          setLoading(false);
        }
        return;
      }

      try {
        setLoading(true);
        setMsg("");

        const res = await fetch(`${LOOKUP_URL}?token=${encodeURIComponent(token)}`);
        const data = await res.json().catch(() => ({}));

        if (!res.ok) {
          if (!ignore) {
            setInvite(null);
            setMsg(data?.error || "Invite not found.");
          }
          return;
        }

        if (!ignore) {
          setInvite(data.invite || null);
        }
      } catch (e) {
        if (!ignore) setMsg(e?.message || "Failed to load invite.");
      } finally {
        if (!ignore) setLoading(false);
      }
    }

    loadInvite();
    return () => { ignore = true; };
  }, [token]);

  const derivedStatus = (() => {
    if (!invite) return "";
    if (invite.status === "accepted") return "accepted";
    if (invite.expiresAtMs && Date.now() > invite.expiresAtMs) return "expired";
    return invite.status || "pending";
  })();

  const acceptInviteForCurrentUser = useCallback(async () => {
    if (!auth.currentUser || !token || !invite) return;

    try {
      setBusy(true);
      setMsg("");

      const idToken = await auth.currentUser.getIdToken();

      const res = await fetch("https://us-central1-lastwishbox-business.cloudfunctions.net/acceptTenantInvite", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${idToken}`
        },
        body: JSON.stringify({ token })
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data?.error || "Failed to accept invite");
      }

      setMsg("Invitation accepted.");
      navigate("/");
    } catch (e) {
      setMsg(e?.message || "Failed to accept invite");
    } finally {
      setBusy(false);
    }
  }, [invite, navigate, token]);


  useEffect(() => {
    let ignore = false;

    async function autoAcceptLoggedInUser() {
      if (!auth.currentUser) return;
      if (!invite) return;
      if (derivedStatus !== "pending") return;

      const currentEmail = String(auth.currentUser.email || "").trim().toLowerCase();
      const invitedEmail = String(invite.email || "").trim().toLowerCase();

      if (currentEmail && invitedEmail && currentEmail !== invitedEmail) {
        if (!ignore) {
          setMsg(`You are signed in as ${currentEmail}, but this invite is for ${invitedEmail}. Please sign out and continue with the invited email.`);
        }
        return;
      }

      try {
        setBusy(true);
        setMsg("Processing your invitation…");
        await acceptInviteForCurrentUser();
        if (!ignore) {
          setMsg("Invitation accepted. Redirecting…");
          setTimeout(() => navigate("/", { replace: true }), 800);
        }
      } catch (e) {
        if (!ignore) setMsg(e?.message || "Failed to accept invite");
      } finally {
        if (!ignore) setBusy(false);
      }
    }

    autoAcceptLoggedInUser();
    return () => { ignore = true; };
  }, [invite, derivedStatus, navigate, token, acceptInviteForCurrentUser]);

  async function handleRegister(e) {
    e.preventDefault();
    setMsg("");

    if (!invite?.email) {
      setMsg("Invite email is missing.");
      return;
    }
    if (!password || password.length < 8) {
      setMsg("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setMsg("Passwords do not match.");
      return;
    }

    try {
      setBusy(true);

      await createUserWithEmailAndPassword(auth, invite.email, password);

      await acceptInviteForCurrentUser();

      setMsg("Account created and invitation accepted. Redirecting…");
      setTimeout(() => navigate("/", { replace: true }), 800);
    } catch (e) {
      setMsg(e?.message || "Could not create your account.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ background: "#f6f7fb", minHeight: "100vh", padding: 24 }}>
      <div style={{ maxWidth: 760, margin: "0 auto" }}>
        <div style={{
          background: "#fff",
          border: "1px solid #e8ebf3",
          borderRadius: 16,
          padding: 24,
          boxShadow: "0 8px 24px rgba(0,0,0,0.04)"
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 20 }}>
            <div>
              <h1 style={{ margin: 0, color: "#2a0516" }}>Join Workspace</h1>
              <p style={{ margin: "6px 0 0", color: "#657899" }}>
                Create your account to join your company workspace on LastWish Box Business.
              </p>
            </div>

            <Link
              to="/"
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                height: 42,
                padding: "0 14px",
                borderRadius: 10,
                border: "1px solid #d6dbe7",
                background: "#fff",
                color: "#2a0516",
                textDecoration: "none",
                fontWeight: 700
              }}
            >
              ← Back
            </Link>
          </div>

          {msg ? (
            <div style={{
              marginBottom: 16,
              background: msg.toLowerCase().includes("accepted") || msg.toLowerCase().includes("created")
                ? "#ecfdf5"
                : "#fff1f2",
              border: "1px solid " + (
                msg.toLowerCase().includes("accepted") || msg.toLowerCase().includes("created")
                  ? "#bbf7d0"
                  : "#fecdd3"
              ),
              color: msg.toLowerCase().includes("accepted") || msg.toLowerCase().includes("created")
                ? "#166534"
                : "#9f1239",
              borderRadius: 12,
              padding: 14
            }}>
              {msg}
            </div>
          ) : null}

          {loading ? (
            <div>Loading invite…</div>
          ) : !invite ? (
            <div>No invite found.</div>
          ) : (
            <>
              <div style={{
                display: "grid",
                gridTemplateColumns: "180px 1fr",
                gap: "10px 16px",
                marginBottom: 20
              }}>
                <strong>Tenant</strong><span>{invite.tenantName || invite.tenantId || "—"}</span>
                <strong>Email</strong><span>{invite.email || "—"}</span>
                <strong>Role</strong><span>{invite.role || "—"}</span>
                <strong>Status</strong><span>{derivedStatus || "—"}</span>
              </div>

              {!auth.currentUser && derivedStatus === "pending" ? (
                <>
                  <form onSubmit={handleRegister} style={{ display: "grid", gap: 12, marginBottom: 16 }}>
                    <div>
                      <label style={{ display: "block", fontSize: 13, fontWeight: 700, marginBottom: 6, color: "#657899" }}>
                        Full name
                      </label>
                      <input
                        type="text"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        placeholder="Your full name"
                        style={{
                          width: "100%",
                          height: 44,
                          borderRadius: 10,
                          border: "1px solid #d6dbe7",
                          padding: "0 12px",
                          fontSize: 14
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ display: "block", fontSize: 13, fontWeight: 700, marginBottom: 6, color: "#657899" }}>
                        Email
                      </label>
                      <input
                        type="email"
                        value={invite.email || ""}
                        readOnly
                        style={{
                          width: "100%",
                          height: 44,
                          borderRadius: 10,
                          border: "1px solid #d6dbe7",
                          padding: "0 12px",
                          fontSize: 14,
                          background: "#f9fafb"
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ display: "block", fontSize: 13, fontWeight: 700, marginBottom: 6, color: "#657899" }}>
                        Password
                      </label>
                      <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Create a password"
                        style={{
                          width: "100%",
                          height: 44,
                          borderRadius: 10,
                          border: "1px solid #d6dbe7",
                          padding: "0 12px",
                          fontSize: 14
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ display: "block", fontSize: 13, fontWeight: 700, marginBottom: 6, color: "#657899" }}>
                        Confirm password
                      </label>
                      <input
                        type="password"
                        value={confirm}
                        onChange={(e) => setConfirm(e.target.value)}
                        placeholder="Confirm your password"
                        style={{
                          width: "100%",
                          height: 44,
                          borderRadius: 10,
                          border: "1px solid #d6dbe7",
                          padding: "0 12px",
                          fontSize: 14
                        }}
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={busy}
                      style={{
                        height: 44,
                        borderRadius: 10,
                        border: "1px solid #2a0516",
                        background: "#2a0516",
                        color: "#fff",
                        fontWeight: 700,
                        cursor: busy ? "default" : "pointer"
                      }}
                    >
                      {busy ? "Creating…" : "Create Account"}
                    </button>
                  </form>

                  <div style={{ color: "#657899" }}>
                    Already have an account?{" "}
                    <Link
                      to={`/login?invite=${encodeURIComponent(token)}&email=${encodeURIComponent(invite.email || "")}`}
                      style={{ color: "#2a0516", fontWeight: 700, textDecoration: "underline" }}
                    >
                      Sign in
                    </Link>
                  </div>
                </>
              ) : derivedStatus !== "pending" ? (
                <div style={{ color: "#657899" }}>
                  This invite cannot be accepted because it is {derivedStatus}.
                </div>
              ) : String(auth.currentUser?.email || "").trim().toLowerCase() !== String(invite.email || "").trim().toLowerCase() ? (
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
                  <div style={{ color: "#657899" }}>
                    You are signed in with a different email.
                  </div>
                  <button
                    type="button"
                    onClick={async () => {
                      await signOut(auth);
                      window.location.reload();
                    }}
                    style={{
                      height: 42,
                      padding: "0 14px",
                      borderRadius: 10,
                      border: "1px solid #2a0516",
                      background: "#fff",
                      color: "#2a0516",
                      fontWeight: 700,
                      cursor: "pointer"
                    }}
                  >
                    Sign out and continue
                  </button>
                </div>
              ) : (
                <div style={{ color: "#657899" }}>
                  Processing your invitation…
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
